/**
 * Real-time broadcasting via Pusher + Laravel Echo.
 *
 * Previously this read `window.Pusher` / `window.Echo`, which nothing ever
 * assigned — so getEcho() always returned null and every realtime subscription
 * in the app was silently dead. The packages are now real dependencies and are
 * imported lazily, so the ~40KB Pusher bundle only downloads for users in an
 * environment where realtime is actually configured.
 *
 * Activation: set VITE_PUSHER_APP_KEY (and VITE_PUSHER_APP_CLUSTER) at build
 * time, and matching PUSHER_* credentials on the API.
 *
 * Usage:
 *   const echo = await getEcho()
 *   if (echo) echo.private(`user.${userId}`).listen('.expense.created', fn)
 */

const PUSHER_KEY     = import.meta.env.VITE_PUSHER_APP_KEY
const PUSHER_CLUSTER = import.meta.env.VITE_PUSHER_APP_CLUSTER || 'eu'

let echoInstance = null
let echoPromise = null

/** Whether realtime is configured for this build. */
export function isRealtimeEnabled() {
  return Boolean(PUSHER_KEY)
}

/**
 * Resolve the shared Echo instance, or null when realtime isn't configured.
 * Concurrent callers share one in-flight import rather than racing to build
 * two connections.
 */
export function getEcho() {
  if (!isRealtimeEnabled()) return Promise.resolve(null)
  if (echoInstance) return Promise.resolve(echoInstance)
  if (echoPromise) return echoPromise

  echoPromise = (async () => {
    try {
      const [{ default: Echo }, { default: Pusher }] = await Promise.all([
        import('laravel-echo'),
        import('pusher-js'),
      ])

      echoInstance = new Echo({
        broadcaster: 'pusher',
        client: new Pusher(PUSHER_KEY, {
          cluster: PUSHER_CLUSTER,
          forceTLS: true,
          authEndpoint: `${import.meta.env.VITE_API_URL}/broadcasting/auth`,
          auth: {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('tabowe_token')}`,
              Accept: 'application/json',
            },
          },
        }),
      })

      return echoInstance
    } catch (err) {
      // A realtime failure must never take the page down — the app works
      // fine without it, just without live updates.
      console.warn('[Tabowe] Realtime unavailable:', err)
      echoPromise = null
      return null
    }
  })()

  return echoPromise
}

export function disconnectEcho() {
  if (echoInstance) {
    echoInstance.disconnect()
    echoInstance = null
  }
  echoPromise = null
}
